import {FormConfigBehavior, FormConfigUrl, FormProps, FormState} from '../../@types/components/Form';
import {
    IFormConfigBehaviors,
    IFormConfigRedirectBehavior,
    IFormConfigSendRequestBehavior
} from '../../@interfaces/components/Form/IFormConfig';
import app from '../../helpers/app';
import {StringArray} from '../../@interfaces/types';
import FormAccessors from './FormAccessors';
import React from 'react';
import {SharedByRefUrlNavigationApiRef} from '../../@types/SharedByRefUrlNavigationApi';
import {IEmptyModel} from '../../@interfaces/IEmptyModel';

export default class FormWithBehaviors<
        Model extends IEmptyModel = IEmptyModel,
        Props extends FormProps<Model> = FormProps<Model>,
        State extends FormState = FormState
> extends FormAccessors<Model, Props, State> {
    state = {} as State;

    protected redirectRef!: React.RefObject<SharedByRefUrlNavigationApiRef>;

    constructor(props: Props) {
        super(props);

        this.redirectRef = React.createRef<SharedByRefUrlNavigationApiRef>();
    }

    protected getBehavior(behaviorName: keyof IFormConfigBehaviors): FormConfigBehavior | null {
        if (!this.config?.behaviors) {
            return null;
        }

        return this.config.behaviors[behaviorName] ?? null;
    }

    protected hasBehavior(behaviorName: keyof IFormConfigBehaviors): boolean {
        return this.getBehavior(behaviorName) !== null;
    }

    protected async applyBehavior(behaviorName: keyof IFormConfigBehaviors, params?: Record<string, any>) {
        const behavior = this.getBehavior(behaviorName);
        if (!behavior) {
            return;
        }

        if (behavior.type === 'send-request') {
            return this.applySendRequestBehavior(behavior, params);
        }

        if (behavior.type === 'redirect') {
            return this.applyRedirectBehavior(behavior, params);
        }
    }

    protected async applySendRequestBehavior(behavior: IFormConfigSendRequestBehavior, params?: Record<string, any>) {
        return this.http.request(this.resolveFormConfigUrl(behavior.action), {
            method: behavior.method,
            headers: {'Content-Type': 'multipart/form-data'},
            body: {
                ...(behavior.additionalData ?? {}),
                ...(params ?? {}),
            }
        });
    }

    protected async applyRedirectBehavior(behavior: IFormConfigRedirectBehavior, params?: Record<string, any>) {
        this.redirectTo(
                this.resolveFormConfigUrl(behavior.to, params, '/')
        );
    }

    protected redirectTo(url: string) {
        this.redirectRef.current?.redirectTo(url);
    }

    protected resolveFormConfigUrl(
            config: FormConfigUrl,
            routeParams?: string[] | StringArray<string>,
            defaultValue = '/'
    ): string {
        if (typeof config === 'string') {
            return config;
        }

        if (!config.route) {
            return defaultValue;
        }

        const route = app('url').getRouteByName(config.route);
        if (!route) {
            return defaultValue;
        }

        const paramsTypeIsArray = [config.params, routeParams].some(list => Array.isArray(list));
        const paramsToArray = (params: string[] | StringArray<string>): string[] => {
            return Array.isArray(params) ? params : Object.values(params);
        };
        const params = paramsTypeIsArray ? [
            ...paramsToArray(config.params || []),
            ...paramsToArray(routeParams || []),
        ] : {...config.params, ...routeParams};

        return route.resolveUrl(params);
    }

    protected get http() {
        return app('http');
    }
}
